import { OverviewReportTab } from '@/pages/ReportV2/OverviewReportTab';
import { RunAttractorsReportTab } from '@/pages/ReportV2/RunReportV2/RunAttractorsReportTab';
import { RunHamstringFunctionReportTab } from '@/pages/ReportV2/RunReportV2/RunHamstringFunctionReportTab';
import { RunKinematicsReportTab } from '@/pages/ReportV2/RunReportV2/RunKinematicsReportTab';
import { RunningStatusReportTab } from '@/pages/ReportV2/RunReportV2/RunningStatusReportTab';
import { RunScissorsReportTab } from '@/pages/ReportV2/RunReportV2/RunScissorsReportTab';
import { RunVariabilityReportTab } from '@/pages/ReportV2/RunReportV2/RunVariabilityReportTab';
/** Report detail page, holding tabs for analysis, kinematics, and overview */
import { IonContent, IonFabButton, IonGrid, IonIcon, IonPage } from '@ionic/react';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';

import { FloatingButton } from '@/components/FloatingButton/FloatingButton';
import { Loading } from '@/components/Loading/Loading';
// Components
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { PageTopBar } from '@/components/PageTopBar/PageTopBar';
import { SegmentButton, SegmentedControl } from '@/components/SegmentedControl';
import { Toast } from '@/components/Toasts/Toast';

import { getTitleAndDescriptionFromReportData } from '@/utils/getTitleAndDescriptionFromReportData';
import { mergeClassNames } from '@/utils/mergeClassNames';

import { createRunReportData } from '@/hooks/useReports';

// Utils
import { useAuthContext } from '@/contexts/authContext';
// Hooks & context
import { useReportContext } from '@/contexts/reportContext';

// Data
import { ReportInfoId } from '@/models';
import { RunReportData } from '@/models/reportV2.model';

// Styles
import styles from '@/pages/ReportV2/Styles/ReportDetailPage.module.css';

interface ParamTypes {
  id: ReportInfoId;
}
/** Report detail page
 * Loading the report data for the given userId and reportInfoId in the url.
 * Switch the tabs from Walk to Run depending on reportType in the loaded report data.
 * @param reportInfoId - the reportInfoId from the url
 */
export const RunReportDetailPageV2 = () => {
  const location = useLocation<{ title: string; description: string; backTarget?: string | null }>();
  const { id } = useParams<ParamTypes>();
  const { getReportData, dataReady, data, setDataReady, resetData, isError } = useReportContext();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('runninganalysis');
  const [runReportData, setRunReportData] = useState<RunReportData | undefined>();
  const role = useAuthContext().state.userState?.role;
  const history = useHistory();
  const [Error, setIsError] = useState(false);
  const [floatingButtons, setFloatingButtons] = useState<React.ReactNode>(null);

  const { title, description, backTarget } = location.state || getTitleAndDescriptionFromReportData(runReportData);

  useEffect(() => {
    getReportData(id);
    // Cleanup the data when the component unmounts
    return () => {
      setLoading(true);
      setRunReportData(undefined);
      setDataReady(false); // Reset dataReady in the context
      resetData();
    };
  }, [id]);

  useEffect(() => {
    if (isError) {
      setIsError(true);
    }
  }, [isError]);

  useEffect(() => {
    if (data) {
      setRunReportData(createRunReportData(data));
    } else {
      setRunReportData(undefined);
    }
  }, [dataReady]);

  useEffect(() => {
    if (runReportData !== undefined && data) {
      setLoading(false);
    }
  }, [runReportData]);

  useEffect(() => {
    if (import.meta.env.MODE !== 'production') console.log('reportData', runReportData);
  }, [runReportData]);

  const isVersionAbove2111 = (currentVersion: string | undefined, targetVersion: [number, number, number]) => {
    if (!currentVersion || typeof currentVersion !== 'string' || targetVersion.length !== 3) return false;

    const versionParts = currentVersion.split('.').map(Number);

    if (versionParts.length !== 3) return false;

    for (let i = 0; i < 3; i++) {
      if (versionParts[i] > targetVersion[i]) return true;
      if (versionParts[i] < targetVersion[i]) return false;
    }

    return true;
  };

  const handleOnSegmentClick = (target: string) => {
    setActiveTab(target);
    history.push(`/reportsV2/Run/${id}/${target}`);
  };

  const shouldShowHamstringSegment = useMemo(
    () => isVersionAbove2111(runReportData?.version, [2, 1, 11]) && role === 'ORYX_Admin',
    [runReportData],
  );

  const segments: SegmentButton[] = [
    { title: 'Analysis', value: 'runninganalysis', onClick: () => handleOnSegmentClick('runninganalysis') },
    { title: 'Running status', value: 'runningStatus', onClick: () => handleOnSegmentClick('runningStatus') },
    { title: 'Scissors', value: 'scissors', onClick: () => handleOnSegmentClick('scissors') },
    { title: 'Kinematics', value: 'kinematics', onClick: () => handleOnSegmentClick('kinematics') },
    { title: 'Variability', value: 'variability', onClick: () => handleOnSegmentClick('variability') },
    // only show the hamstring function segment if the user is an admin and the report is above 2.1.11
    ...(shouldShowHamstringSegment
      ? [{ title: 'Hamstring function', value: 'hamstring', onClick: () => handleOnSegmentClick('hamstring') }]
      : []),
    { title: 'Overview', value: 'overview', onClick: () => handleOnSegmentClick('overview') },
  ];

  return (
    <>
      <IonPage>
        <PageHeader
          isLoading={loading}
          topSlot={
            <PageTopBar
              backTarget={backTarget ? backTarget : '/reports'}
              description={description}
              title={title}
              variant='simple'
            />
          }>
          <SegmentedControl segments={segments} value={activeTab} variant='default' />
        </PageHeader>
        <div className={styles.background}>
          <IonContent className={mergeClassNames('ion-padding', styles.content)} color='light' id='main'>
            <IonGrid fixed style={{ height: '100%' }}>
              {loading && <Loading />}
              {runReportData && (
                <>
                  <Suspense fallback={<Loading />}>
                    {activeTab === 'runninganalysis' && (
                      <RunAttractorsReportTab tabData={runReportData.runAttractorData} />
                    )}
                    {activeTab === 'runningStatus' && (
                      <RunningStatusReportTab tabData={runReportData.runningStatusData} />
                    )}
                    {activeTab === 'scissors' && <RunScissorsReportTab tabData={runReportData.runScissorsData} />}
                    {activeTab === 'kinematics' && (
                      <RunKinematicsReportTab
                        setFloatingButtons={setFloatingButtons}
                        tabData={runReportData.runKinematicData}
                      />
                    )}
                    {activeTab === 'variability' && (
                      <RunVariabilityReportTab
                        setFloatingButtons={setFloatingButtons}
                        tabData={runReportData.runVariabilityData}
                      />
                    )}
                    {activeTab === 'hamstring' && (
                      <RunHamstringFunctionReportTab
                        setFloatingButtons={setFloatingButtons}
                        tabData={runReportData.runHamstringFunctionData!}
                      />
                    )}
                    {activeTab === 'overview' && (
                      <OverviewReportTab
                        fileInfo={runReportData.fileInfo}
                        id={id}
                        reportType={runReportData.reportType}
                      />
                    )}
                  </Suspense>
                </>
              )}
            </IonGrid>
          </IonContent>
          {floatingButtons && (
            <FloatingButton horizontal='end' side='top' slot='fixed' vertical='bottom'>
              {floatingButtons}
            </FloatingButton>
          )}
        </div>
      </IonPage>
      <Toast isOpen={Error} onDidDismiss={() => setIsError(false)} type={'warning'} />
    </>
  );
};
